import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SelectOption } from '@/components/types';
import { Combobox, Input, Loader, useCombobox } from '@mantine/core';
import practistSelectClasses from './PractistSelect.module.css';

export interface PractistSelectClassNames {
  // The container that holds the label and inputContainer
  // Technically a Mantine Input.Wrapper component
  labelAndInputContainer?: string;
  // The container that holds the selected label, placeholder, and input
  // A div element
  inputContainer?: string;
  // The input element
  input?: string;
  // The selected option's label, displayed when an option is selected and no
  // search is in progress
  selectedLabel?: string;
}

export interface PractistSelectStyles {
  // The container that holds the label and inputContainer
  // `Input.Wrapper` component
  labelAndInputContainer?: React.CSSProperties;
  // The container that holds the selected label, placeholder, and input
  // `div` element
  inputContainer?: React.CSSProperties;
  // The label element
  label?: React.CSSProperties;
  // The input element
  input?: React.CSSProperties;
  // The selected option's label, displayed when an option is selected and no
  // search is in progress
  selectedLabel?: React.CSSProperties;
  // The placeholder
  placeholder?: React.CSSProperties;
  // An option in the dropdown
  // `Combobox.Option` component
  option?: React.CSSProperties;
}

/**
 * Our custom select component
 *
 * Build on Mantine's Combobox component but mimicks many of the behaviors of
 * the react-select component.
 */
const PractistSelect = <T extends SelectOption = SelectOption<string>>({
  size = 'sm',
  inputRef: outerInputRef,
  label,
  ariaLabel,
  placeholder,
  classNames,
  styles,
  dropdownWidth,
  value,
  data,
  disabled,
  required,
  onSearchChange,
  clearable,
  loading,
  onOptionSelected,
  onFocus,
  onBlur,
  renderOption,
  emptyMessage = 'No results found',
  maxDropdownOptions = 50,
  withinPortal = true,
}: {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  inputRef?: React.RefObject<HTMLInputElement>;
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  classNames?: PractistSelectClassNames;
  styles?: PractistSelectStyles;
  dropdownWidth?: string;
  value?: string;
  data: T[];
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  loading?: boolean;
  maxDropdownOptions?: number;
  withinPortal?: boolean;
  /**
   * The onSearchChange callback will be called with the current search value
   * when the user types.
   *
   * If provided, the caller is responsible for filtering the options based on
   * the search value, and then updating the `data` prop with the filtered
   * options.
   *
   * If not provided, the component will filter the `data` options based on the
   * search value.
   */
  onSearchChange?: (searchValue: string) => void;
  // The onChange callback will be called with the selected value when the user
  // selects an option.
  onOptionSelected: (
    value: string,
    selectionMethod: 'keyboard' | 'mouse',
  ) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  renderOption?: (option: T, isActive: boolean) => React.ReactNode;
  emptyMessage?: string;
}) => {
  // State

  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<T[]>(data);
  const maybeInputRef = useRef<HTMLInputElement>(null);
  const inputRef = outerInputRef ?? maybeInputRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownId = useId();
  // Track if the last selection was made via keyboard (Enter key)
  const wasSelectedWithKeyboardRef = useRef(false);

  // Use a ref to hold the current value so the onDropdownOpen callback always
  // has access to the latest value (avoiding stale closure)
  const valueRef = useRef(value);
  useLayoutEffect(() => {
    valueRef.current = value;
  });

  // Misc hooks

  const combobox = useCombobox({
    onDropdownOpen: () => {
      if (!valueRef.current) {
        combobox.selectFirstOption();
      } else {
        combobox.selectActiveOption();
      }
    },
  });

  // Memoized values

  const optionsForDropdown = useMemo(() => {
    if (onSearchChange) {
      return data;
    }
    return filteredOptions;
  }, [data, filteredOptions, onSearchChange]);

  const selectedOption = useMemo(
    () => data.find((opt) => opt.value === value),
    [data, value],
  );

  // Determines whether to show the selected option's label as static text
  const showSelectedLabel = useMemo(
    () => !!selectedOption && !searchValue,
    [selectedOption, searchValue],
  );

  // Effects

  // UPDATE_FILTERED_OPTIONS_EFFECT
  useEffect(() => {
    if (onSearchChange) {
      // Don't filter if onSearchChange is provided
      return;
    }
    const searchValueIsEmpty = searchValue.trim() === '';
    const updatedFilteredOptions = searchValueIsEmpty
      ? data
      : data.filter((opt) =>
          opt.label.toLowerCase().includes(searchValue.toLowerCase()),
        );

    setFilteredOptions(updatedFilteredOptions);
  }, [data, onSearchChange, searchValue]);

  // CALLBACK_SEARCH_CHANGE_EFFECT
  useEffect(() => {
    onSearchChange?.(searchValue);
  }, [onSearchChange, searchValue]);

  // UPDATE_SELECTED_OPTION_EFFECT
  //
  // This effect highlights the most-relevant option in the dropdown after the
  // filtered list of options is updated. We do it here instead of in
  // UPDATE_FILTERED_OPTIONS_EFFECT because, in that effect, the combobox has
  // not yet updated its internal state and so the selected option is not
  // visually highlighted.
  //
  // Expected behavior:
  //   Given:
  //     - data: [abc, acd, abd]
  //     - value: acd
  //   Action A:
  //     - user types 'a'
  //   Result A:
  //     - dropdown options: [abc, acd, abd]
  //     - highlighted option: acd
  //     - reason: the selected option is included in the filtered options, so
  //       it is highlighted.
  //   Action B:
  //     - user types 'b' (searchValue is 'ab')
  //   Result B:
  //     - dropdown options:
  //     - highlighted option: abc
  //     - reason: the selected option has been filtered out, so the first
  //       available option is highlighted.
  //   Action C:
  //     - user types 'Backspace' (searchValue is 'a')
  //   Result C:
  //     - dropdown options: [abc, acd, abd]
  //     - highlighted option: acd
  //     - reason: the selected option has been un-filtered, so it again becomes
  //       the highlighted option.
  useEffect(() => {
    const activeOption = optionsForDropdown.find(
      (opt) => opt.value === selectedOption?.value,
    );
    if (activeOption) {
      combobox.selectActiveOption();
    } else {
      combobox.selectFirstOption();
    }
  }, [combobox, optionsForDropdown, selectedOption]);

  // Handlers

  const handleOptionSelect = (optionValue: string) => {
    const option = optionsForDropdown.find((opt) => opt.value === optionValue);
    if (option) {
      setSearchValue('');
      const wasKeyboard = wasSelectedWithKeyboardRef.current;
      wasSelectedWithKeyboardRef.current = false; // Reset for next selection
      onOptionSelected(option.value, wasKeyboard ? 'keyboard' : 'mouse');
      combobox.closeDropdown();
    }
  };

  const handleInputChange = (newValue: string) => {
    setSearchValue(newValue);

    // Only open if user is actually typing (search differs from selected label)
    if (newValue) {
      combobox.openDropdown();
    }
  };

  const handleContainerKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    // Space will technically open the dropdown without this check, but we don't
    // want to add the ' '. If the user has keyed the space bar while the input
    // is inactive and the dropdown is closed, then we're assumeing that the
    // intention is to open the dropdown, not to type a literal space.
    if (event.key === ' ' && !combobox.dropdownOpened) {
      event.preventDefault();
      combobox.openDropdown();
      return;
    }

    if (clearable && (event.key === 'Backspace' || event.key === 'Delete')) {
      event.preventDefault();
      event.stopPropagation();
      onOptionSelected('', 'keyboard');
      setSearchValue('');
      combobox.closeDropdown();
      return;
    }

    // Escape closes dropdown or blurs
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (combobox.dropdownOpened) {
        combobox.closeDropdown();
      } else {
        event.currentTarget.blur();
      }
      return;
    }

    // Track Enter key press so that 'keyboard' selection type can be included
    // in the onOptionSelected callback.
    if (event.key === 'Enter' && combobox.dropdownOpened) {
      wasSelectedWithKeyboardRef.current = true;
      return;
    }

    // Arrow down opens dropdown
    if (event.key === 'ArrowDown' && !combobox.dropdownOpened) {
      event.preventDefault();
      combobox.openDropdown();
      return;
    }

    // Printable character: focus input and let it handle the keystroke
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
      inputRef.current?.focus();
      // The character will be typed into the now-focused input
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setSearchValue('');
      combobox.closeDropdown();
      // Return focus to container
      containerRef.current?.focus();
      return;
    }

    // Track Enter key press so that 'keyboard' selection type can be included
    // in the onOptionSelected callback.
    if (event.key === 'Enter' && combobox.dropdownOpened) {
      wasSelectedWithKeyboardRef.current = true;
    }

    // Shift+Tab: temporarily remove container from tab order so browser skips it
    if (event.key === 'Tab' && event.shiftKey && containerRef.current) {
      containerRef.current.tabIndex = -1;
      // Restore tabIndex after the browser completes focus navigation.
      // Note: This handler can only fire when isDisabled is false (since the
      // input is disabled otherwise), but we include the check defensively in
      // case isDisabled changes before the callback runs. React will also
      // correct the tabIndex on the next render via JSX.
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.tabIndex = disabled ? -1 : 0;
        }
      });
      // Don't prevent default - let the browser handle navigation naturally
    }
  };

  // Important: This list should not be used for anything other than rendering
  // the options list. It may not include all valid options, so any logic that
  // depends on the full list of options should use the `optionsForDropdown` list.
  const slicedOptionsForDropdown = useMemo(() => {
    if (value && optionsForDropdown.length > maxDropdownOptions) {
      const valueIndex = optionsForDropdown.findIndex(
        (opt) => opt.value === value,
      );
      if (valueIndex !== -1) {
        // Include a few options before and after the selected option.

        const startIndex = Math.max(0, valueIndex - 2);

        const endIndex = Math.min(
          optionsForDropdown.length,
          startIndex + maxDropdownOptions,
        );
        return optionsForDropdown.slice(startIndex, endIndex);
      }
    }
    return optionsForDropdown.slice(0, maxDropdownOptions);
  }, [optionsForDropdown, maxDropdownOptions, value]);

  const optionStyle = {
    fontSize: `var(--mantine-font-size-${size})`,
    ...styles?.option,
  };

  const options = slicedOptionsForDropdown.map((opt) => (
    <Combobox.Option
      className={practistSelectClasses.option}
      key={opt.value}
      value={opt.value}
      active={opt.value === value}
      style={optionStyle}
    >
      {renderOption ? renderOption(opt, opt.value === value) : opt.label}
    </Combobox.Option>
  ));

  // Shared grid cell style for overlapping elements
  const gridCellStyle = {
    gridArea: '1 / 1 / 2 / 2',
  };

  const labelAndInputContainerStyle = {
    width: '100%',
    fontSize: `var(--mantine-font-size-${size})`,
    ...styles?.labelAndInputContainer,
  };

  const labelStyle = {
    fontSize: `var(--mantine-font-size-${size})`,
    ...styles?.label,
  };

  const inputContainerStyle = {
    height: 'auto',
    paddingBlock: `5.5px`, // 5.5px is a magic number that matches Mantine's DatePickerInput component
    ...styles?.inputContainer,
  };

  const inputStyle = {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    width: '100%',
    padding: 0,
    // Hide caret only when showing selected label (has value, no search)
    caretColor: showSelectedLabel ? 'transparent' : 'auto',
    fontSize: `var(--mantine-font-size-${size})`,
    ...styles?.input,
  };

  const placeholderStyle = {
    color: 'var(--mantine-color-placeholder)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: `var(--mantine-font-size-${size})`,
    ...styles?.placeholder,
  };

  const selectedLabelStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: `var(--mantine-font-size-${size})`,
    ...styles?.selectedLabel,
  };

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSelect}
      offset={0}
      dropdownPadding={0}
      shadow='md'
      width={dropdownWidth}
      withinPortal={withinPortal}
    >
      <Combobox.Target>
        <Input.Wrapper
          label={label}
          className={classNames?.labelAndInputContainer}
          styles={{
            root: {
              ...labelAndInputContainerStyle,
            },
            label: labelStyle,
          }}
          required={required}
          onFocus={onFocus}
        >
          <div
            ref={containerRef}
            className={`${practistSelectClasses.inputContainer} ${
              classNames?.inputContainer ?? ''
            }`}
            style={inputContainerStyle}
            tabIndex={disabled ? -1 : 0}
            role='combobox'
            aria-expanded={combobox.dropdownOpened}
            aria-haspopup='listbox'
            aria-controls={dropdownId}
            aria-label={ariaLabel ?? label}
            aria-required={required}
            aria-disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (combobox.dropdownOpened) {
                combobox.closeDropdown();
              } else {
                combobox.openDropdown();
                inputRef.current?.focus();
              }
            }}
            onKeyDown={handleContainerKeyDown}
            onFocus={(event) => {
              // Only auto-focus the input if focus is coming from OUTSIDE the component.
              // If relatedTarget (previous focused element) is inside this container,
              // it means focus is moving internally (e.g., from input back to container
              // via Shift+Tab), so we should let the browser handle navigation normally.
              const isFocusFromWithin = event.currentTarget.contains(
                event.relatedTarget as Node,
              );
              if (!isFocusFromWithin && !value) {
                inputRef.current?.focus();
              }
              onFocus?.();
            }}
            onBlur={(event) => {
              // Only close if focus left the container entirely (not to the input)
              if (!event.currentTarget.contains(event.relatedTarget)) {
                combobox.closeDropdown();
                setSearchValue('');
                onBlur?.();
              }
            }}
          >
            {/* Selected label - rendered first, sits behind input */}
            {showSelectedLabel && (
              <span
                style={{
                  ...gridCellStyle,
                  ...selectedLabelStyle,
                }}
                className={classNames?.selectedLabel}
              >
                {selectedOption?.label}
              </span>
            )}

            {/* Placeholder - shown when no value and no search */}
            {!showSelectedLabel && !searchValue && (
              <span
                style={{
                  ...gridCellStyle,
                  ...placeholderStyle,
                }}
              >
                {placeholder}
              </span>
            )}

            {/* Input - rendered last, sits on top, not directly tabbable */}
            <input
              ref={inputRef}
              tabIndex={-1}
              style={{
                ...gridCellStyle,
                ...inputStyle,
              }}
              className={classNames?.input}
              value={searchValue}
              onChange={(event) => handleInputChange(event.currentTarget.value)}
              onKeyDown={handleInputKeyDown}
              disabled={disabled}
              aria-autocomplete='list'
            />
            {clearable && !!value ? (
              <Combobox.ClearButton
                key='clear-button'
                size={size}
                style={{
                  gridArea: '1 / 2 / 2 / 3',
                  height: '20px',
                  width: '20px',
                  minHeight: '20px',
                  minWidth: '20px',
                }}
                onClear={() => {
                  onOptionSelected('', 'mouse');
                  setSearchValue('');
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                className={practistSelectClasses.clearButton}
              />
            ) : loading ? (
              <Loader size={18} style={{ gridArea: '1 / 2 / 2 / 3' }} />
            ) : (
              <Combobox.Chevron
                style={{ gridArea: '1 / 2 / 2 / 3' }}
                className={practistSelectClasses.chevron}
              />
            )}
          </div>
        </Input.Wrapper>
      </Combobox.Target>

      <Combobox.Dropdown
        className={practistSelectClasses.dropdown}
        id={dropdownId}
        role='listbox'
      >
        <Combobox.Options
          mah={400}
          style={{ overflowY: 'auto' }}
          onMouseDown={() => {
            // Track that selection was made via mouse (not keyboard)
            wasSelectedWithKeyboardRef.current = false;
          }}
        >
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty style={optionStyle}>{emptyMessage}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default PractistSelect;
